import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, X, Package, Tag, Gift, ShoppingCart, Mail } from "lucide-react";
import { api, cad, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { Switch } from "@/components/ui/switch";

const CATS = ["Gifts", "Toys", "Decor", "Stationery", "New / Other"];
const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "promos", label: "Promo Codes", icon: Tag },
  { id: "giftcards", label: "Gift Cards", icon: Gift },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "newsletter", label: "Subscribers", icon: Mail },
];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  if (!user || user.role !== "admin")
    return <Layout><div className="py-40 text-center text-charcoal/50">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
        <p className="eyebrow">Store management</p>
        <h1 className="mt-2 font-serif text-4xl text-charcoal mb-8">Admin Dashboard</h1>
        <StoreToggle />
        <div className="flex flex-wrap gap-2 mb-10 border-b border-soft-border">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-display uppercase tracking-[0.12em] border-b-2 -mb-px transition-colors ${tab === t.id ? "border-gold text-charcoal" : "border-transparent text-charcoal/50 hover:text-charcoal"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "products" && <ProductsAdmin />}
        {tab === "promos" && <PromosAdmin />}
        {tab === "giftcards" && <GiftCardsAdmin />}
        {tab === "orders" && <OrdersAdmin />}
        {tab === "newsletter" && <NewsletterAdmin />}
      </div>
    </Layout>
  );
}

const empty = { name: "", category: "Gifts", price: 0, description: "", images: [], stock: 0, badges: [], featured: false, bestseller: false };

function StoreToggle() {
  const { storeOpen, refresh } = useStore();
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { store_open: !storeOpen });
      await refresh();
      toast.success(!storeOpen ? "Store is now OPEN — customers can check out 🎉" : "Store is now closed to orders");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={`mb-8 rounded-sm border p-5 flex items-center justify-between gap-4 flex-wrap ${storeOpen ? "border-gold/40 bg-gold-light" : "border-soft-border bg-white"}`} data-testid="store-toggle-card">
      <div>
        <p className="font-serif text-xl text-charcoal">
          {storeOpen ? "🟢 Store is Open" : "🌱 Store is Closed (browsing only)"}
        </p>
        <p className="text-sm text-charcoal/60 mt-1 max-w-xl">
          {storeOpen
            ? "Customers can add to cart and complete checkout. Turn this off while you update inventory."
            : "Customers can browse everything, but checkout and gift-card purchases are disabled. Turn this on once your real inventory is ready."}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs eyebrow text-charcoal/60">{storeOpen ? "Open" : "Closed"}</span>
        <Switch data-testid="store-open-switch" checked={storeOpen} onCheckedChange={toggle} disabled={saving} />
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [waitlist, setWaitlist] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/admin/waitlist").then(({ data }) => setWaitlist(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock),
        badges: typeof form.badges === "string" ? form.badges.split(",").map((s) => s.trim()).filter(Boolean) : form.badges };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted"); load();
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const badgesStr = Array.isArray(form.badges) ? form.badges.join(", ") : form.badges;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-charcoal/60">{products.length} products</p>
        <Button data-testid="admin-add-product-btn" onClick={openNew} className="bg-charcoal text-white rounded-none hover:bg-gold font-display"><Plus className="h-4 w-4 mr-2" /> Add Product</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-soft-border rounded-sm p-4 flex gap-4" data-testid={`admin-product-${p.id}`}>
            <div className="h-20 w-16 shrink-0 overflow-hidden rounded-sm bg-blush">
              {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-charcoal truncate">{p.name}</p>
              <p className="text-xs text-charcoal/50">{p.category} · {cad(p.price)}</p>
              <p className="text-xs text-charcoal/50">Stock: {p.stock}</p>
              {waitlist[p.id] > 0 && (
                <p className="text-[0.65rem] text-gold mt-0.5" data-testid={`waitlist-${p.id}`}>
                  🌱 {waitlist[p.id]} waiting for restock
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="text-charcoal/60 hover:text-gold"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`} className="text-charcoal/60 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-page max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 w-full rounded-none border border-charcoal/20 bg-white px-3 text-sm">
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Price (CAD)"><Input data-testid="pf-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none" /></Field>
            </div>
            <Field label="Stock"><Input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" /></Field>
            <Field label="Description"><Textarea data-testid="pf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" /></Field>
            <Field label="Badges (comma separated)"><Input data-testid="pf-badges" value={badgesStr} onChange={(e) => setForm({ ...form, badges: e.target.value })} placeholder="New, Best Seller" className="rounded-none" /></Field>
            <Field label="Images">
              <div className="flex flex-wrap gap-2">
                {form.images.map((im, i) => (
                  <div key={i} className="relative h-16 w-14 rounded-sm overflow-hidden">
                    <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })} className="absolute top-0 right-0 bg-charcoal/70 text-white p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pf-upload-btn" className="h-16 w-14 border border-dashed border-charcoal/30 grid place-items-center text-charcoal/50 hover:border-gold">
                  <Upload className="h-4 w-4" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
              </div>
              <Input value="" onChange={(e) => e.target.value && setForm({ ...form, images: [...form.images, e.target.value] })} placeholder="…or paste an image URL and press enter" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { setForm({ ...form, images: [...form.images, e.target.value] }); e.target.value = ""; } }} className="rounded-none mt-2 text-xs" data-testid="pf-image-url" />
            </Field>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.bestseller} onChange={(e) => setForm({ ...form, bestseller: e.target.checked })} data-testid="pf-bestseller" /> Best Seller</label>
            </div>
            <Button data-testid="pf-save-btn" onClick={save} className="w-full bg-charcoal text-white rounded-none hover:bg-gold font-display">Save Product</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromosAdmin() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, active: true });
  const load = () => api.get("/admin/promos").then(({ data }) => setPromos(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/admin/promos", { ...form, value: parseFloat(form.value) });
      toast.success("Promo saved"); setForm({ code: "", type: "percent", value: 10, active: true }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (code) => { await api.delete(`/admin/promos/${code}`); toast.success("Deleted"); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-white border border-soft-border rounded-sm p-6 space-y-4 h-fit">
        <h2 className="font-serif text-xl">New Promo Code</h2>
        <Field label="Code"><Input data-testid="promo-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="promo-type" className="h-10 w-full rounded-none border border-charcoal/20 bg-white px-3 text-sm">
              <option value="percent">Percent %</option>
              <option value="fixed">Fixed $</option>
            </select>
          </Field>
          <Field label="Value"><Input data-testid="promo-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-none" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        <Button data-testid="promo-save-btn" onClick={save} className="w-full bg-charcoal text-white rounded-none hover:bg-gold font-display">Save</Button>
      </div>
      <div className="space-y-3">
        {promos.map((p) => (
          <div key={p.code} className="bg-white border border-soft-border rounded-sm p-4 flex justify-between items-center" data-testid={`promo-row-${p.code}`}>
            <div>
              <p className="font-serif text-lg text-charcoal">{p.code}</p>
              <p className="text-xs text-charcoal/50">{p.type === "percent" ? `${p.value}% off` : `$${p.value} off`} · {p.active ? "Active" : "Inactive"}</p>
            </div>
            <button onClick={() => del(p.code)} className="text-charcoal/50 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GiftCardsAdmin() {
  const [cards, setCards] = useState([]);
  useEffect(() => { api.get("/admin/giftcards").then(({ data }) => setCards(data)); }, []);
  return (
    <div className="space-y-3">
      {cards.length === 0 && <p className="text-charcoal/50 text-sm">No gift cards issued yet.</p>}
      {cards.map((c) => (
        <div key={c.code} className="bg-white border border-soft-border rounded-sm p-4 flex justify-between items-center flex-wrap gap-2" data-testid={`giftcard-row-${c.code}`}>
          <div>
            <p className="font-serif text-lg text-charcoal">{c.code}</p>
            <p className="text-xs text-charcoal/50">To: {c.recipient_email || c.buyer_email || "—"} · Issued {new Date(c.created_at).toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-charcoal/50">Balance</p>
            <p className="font-serif text-lg text-charcoal">{cad(c.balance)} <span className="text-xs text-charcoal/40">/ {cad(c.amount)}</span></p>
          </div>
        </div>
      ))}
    </div>
  );
}

function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);
  const STATUSES = ["confirmed", "shipped", "delivered", "cancelled"];
  const setStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success(status === "confirmed" ? "Status updated" : `Marked ${status} · customer emailed`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      {orders.length === 0 && <p className="text-charcoal/50 text-sm">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.order_id} className="bg-white border border-soft-border rounded-sm p-5" data-testid={`admin-order-${o.order_id}`}>
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <p className="font-serif text-lg text-charcoal">#{o.order_id}</p>
              <p className="text-xs text-charcoal/50">{o.customer_name} · {o.customer_email}</p>
              <p className="text-xs text-charcoal/50">{new Date(o.created_at).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <select
                data-testid={`order-status-select-${o.order_id}`}
                value={o.status}
                onChange={(e) => setStatus(o.order_id, e.target.value)}
                className="h-9 rounded-none border border-charcoal/20 bg-white px-3 text-xs capitalize font-display tracking-wide"
              >
                {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
              <p className="font-serif text-lg text-charcoal mt-2">{cad(o.total)}</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-charcoal/60">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
          {o.shipping_address && <p className="mt-1 text-xs text-charcoal/45">Ship to: {o.shipping_address.line1}, {o.shipping_address.city} {o.shipping_address.province} {o.shipping_address.postal}</p>}
        </div>
      ))}
    </div>
  );
}

function NewsletterAdmin() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then(({ data }) => setSubs(data)); }, []);
  return (
    <div className="bg-white border border-soft-border rounded-sm p-6">
      <p className="text-sm text-charcoal/60 mb-4">{subs.length} subscribers</p>
      <div className="space-y-2">
        {subs.map((s) => (
          <div key={s.email} className="flex justify-between text-sm border-b border-soft-border pb-2" data-testid={`sub-${s.email}`}>
            <span className="text-charcoal">{s.email}</span>
            <span className="text-charcoal/40 text-xs">{new Date(s.created_at).toLocaleDateString()}</span>
          </div>
        ))}
        {subs.length === 0 && <p className="text-charcoal/50 text-sm">No subscribers yet.</p>}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs eyebrow text-charcoal/70">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
